import { useState, useEffect, useMemo } from 'react'
import { NavLink } from 'react-router'
import { usePodcast } from '../contexts/PodcastContext'
import { useAuth } from '../contexts/AuthContext'
import { getCastbackAlerts } from '../lib/castbackApi'
import { GraspPodIvyIcon } from './icons/GraspPodIcons'
import { useIvyDock } from '../contexts/IvyDockContext'
import type { TeamScopeKey } from '../types/database'
import {
  LayoutDashboard,
  Radio,
  Users,
  Activity,
  Scissors,
  RotateCcw,
  Mic,
  DollarSign,
  Radar,
  Rocket,
  Brain,
  PieChart,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Share2,
  X,
  MonitorPlay,
  TrendingUp,
  Tv,
} from 'lucide-react'

type NavItem = {
  to: string
  label: string
  Icon: typeof LayoutDashboard
  scope?: TeamScopeKey
}

const navGroups: { label: string; items: NavItem[] }[] = [
  {
    label: 'COMMAND CENTER',
    items: [
      { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
    ],
  },
  {
    label: 'INTELLIGENCE',
    items: [
      { to: '/signalcast', label: 'SignalCast', Icon: Radio, scope: 'signalcast' },
      { to: '/trends', label: 'Trends', Icon: TrendingUp, scope: 'signalcast' },
      { to: '/youtube', label: 'YouTube Intel', Icon: MonitorPlay, scope: 'youtube_analytics' },
      { to: '/guests', label: 'Guest Intelligence', Icon: Users, scope: 'guests' },
      { to: '/retention', label: 'Episode DNA', Icon: Activity, scope: 'retention' },
      { to: '/brain', label: 'Episode Brain', Icon: Brain, scope: 'episodes' },
      { to: '/reality', label: 'Reality Trends', Icon: Tv, scope: 'reality' },
    ],
  },
  {
    label: 'CONTENT',
    items: [
      { to: '/clips', label: 'Clip Intelligence', Icon: Scissors, scope: 'clips' },
      { to: '/castback', label: 'CastBack', Icon: RotateCcw, scope: 'castback' },
      { to: '/voice', label: 'Voice Identity', Icon: Mic, scope: 'voice' },
      { to: '/monetization', label: 'Monetization', Icon: DollarSign, scope: 'monetization' },
    ],
  },
  {
    label: 'LIVE & NETWORK',
    items: [
      { to: '/launch', label: 'Live Launch', Icon: Rocket, scope: 'launch' },
      { to: '/competitive', label: 'Competitive Radar', Icon: Radar, scope: 'competitive' },
      { to: '/audience', label: 'Audience', Icon: PieChart, scope: 'dashboard' },
      { to: '/integration', label: 'Integration', Icon: Share2, scope: 'platforms' },
    ],
  },
]

interface SidebarProps {
  collapsed: boolean
  onCollapse: () => void
  mobileOpen: boolean
  onMobileToggle: () => void
}

export default function Sidebar({ collapsed, onCollapse, mobileOpen, onMobileToggle }: SidebarProps) {
  const { selectedPodcast } = usePodcast()
  const { accountType, permissions } = useAuth()
  const { toggleDock } = useIvyDock()
  const expanded = !collapsed || mobileOpen

  // Filter nav for team_members: drop items whose scope is not granted.
  // Items without a `scope` (Dashboard) stay visible for everyone.
  const visibleGroups = useMemo(() => {
    if (accountType !== 'team_member') return navGroups
    return navGroups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) => !item.scope || permissions?.[item.scope] === true,
        ),
      }))
      .filter((group) => group.items.length > 0)
  }, [accountType, permissions])

  // BI-C4: CastBack alert badge — must not block render
  const [castbackBadge, setCastbackBadge] = useState(0)
  useEffect(() => {
    getCastbackAlerts({ status: 'active', limit: 1 })
      .then(data => setCastbackBadge(data.total))
      .catch(() => { /* silent — badge simply doesn't render */ })
  }, [])

  const handleNavClick = () => {
    if (mobileOpen) onMobileToggle()
  }

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={onMobileToggle}
        />
      )}

      <aside
        className={`fixed top-0 left-0 h-screen bg-gray-800 border-r border-white/[0.06] flex flex-col transition-all duration-200 z-40
          ${collapsed ? 'lg:w-16' : 'lg:w-60'}
          ${mobileOpen ? 'w-60 translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="px-5 pt-6 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="shrink-0">
              <defs>
                <linearGradient id="sidebarLogoG" x1="0" y1="0" x2="28" y2="28" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#A78BFA"/>
                  <stop offset="100%" stopColor="#7DD3FC"/>
                </linearGradient>
              </defs>
              <path d="M14 2C7.373 2 2 7.373 2 14s5.373 12 12 12" stroke="url(#sidebarLogoG)" strokeWidth="3.5" strokeLinecap="round" fill="none"/>
              <circle cx="14" cy="14" r="3.5" fill="url(#sidebarLogoG)"/>
            </svg>
            {expanded && (
              <span className="text-[17px] font-extrabold tracking-tight font-display">
                <span className="text-gray-100">Grasp</span>
                <span className="bg-gradient-to-r from-[#A78BFA] to-[#7DD3FC] bg-clip-text text-transparent">Pod</span>
              </span>
            )}
          </div>
          {mobileOpen && (
            <button
              onClick={onMobileToggle}
              className="lg:hidden text-gray-400 hover:text-gray-100 transition-colors"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Show Card */}
        {expanded && selectedPodcast && (
          <div className="px-3.5 pb-4 border-b border-white/[0.06] mb-2">
            <div className="bg-gray-700 border border-white/[0.06] rounded-xl px-3 py-2.5 flex items-center gap-2.5 cursor-pointer hover:border-teal-accent/20 transition-colors">
              <div className="w-9 h-9 rounded-[10px] bg-gradient-to-br from-teal-accent to-brand-blue flex items-center justify-center shrink-0">
                <span className="text-[13px] font-bold text-[#06060E]">
                  {selectedPodcast.title.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-gray-100 truncate">{selectedPodcast.title}</div>
                <div className="text-[10px] text-gray-600">{selectedPodcast.total_episodes ?? 0} episodes</div>
              </div>
              <ChevronDown size={14} className="text-gray-600 shrink-0" />
            </div>
          </div>
        )}

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto pb-2">
          {visibleGroups.map((group) => (
            <div key={group.label} className="pt-3 pb-1">
              {expanded && (
                <div className="text-[9px] font-bold text-gray-600 uppercase tracking-[2.5px] px-5 mb-1">
                  {group.label}
                </div>
              )}
              {group.items.map(({ to, label, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  title={!expanded ? label : undefined}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 py-[9px] border-l-[3px] transition-colors text-[13px] font-medium ${
                      !expanded ? 'px-5 justify-center' : 'px-5'
                    } ${
                      isActive
                        ? 'border-teal-accent bg-teal-accent/[0.04] text-gray-100'
                        : 'border-transparent text-gray-500 hover:text-gray-100 hover:bg-white/[0.03]'
                    }`
                  }
                >
                  <Icon size={16} className="shrink-0 opacity-60" />
                  {expanded && <span>{label}</span>}
                  {expanded && to === '/castback' && castbackBadge > 0 && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        background: 'rgba(255,107,107,0.15)',
                        color: '#FF6B6B',
                        fontSize: '10px',
                        fontWeight: 700,
                        padding: '1px 6px',
                        borderRadius: '8px',
                      }}
                    >
                      {castbackBadge}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}

          {/* Settings */}
          <div className="mt-1 mx-5 border-t border-white/[0.06]" />
          <NavLink
            to="/settings"
            title={!expanded ? 'Settings' : undefined}
            onClick={handleNavClick}
            className={({ isActive }) =>
              `flex items-center gap-2.5 py-[9px] border-l-[3px] transition-colors text-[13px] font-medium mt-1 ${
                !expanded ? 'px-5 justify-center' : 'px-5'
              } ${
                isActive
                  ? 'border-teal-accent bg-teal-accent/[0.04] text-gray-100'
                  : 'border-transparent text-gray-500 hover:text-gray-100 hover:bg-white/[0.03]'
              }`
            }
          >
            <Settings size={16} className="shrink-0 opacity-60" />
            {expanded && <span>Settings</span>}
          </NavLink>
        </nav>

        {/* Ivy FAB — Phase 2A brand mark; Phase 2D wires to IvyDockContext */}
        <div className={`px-4 pb-4 ${!expanded ? 'flex justify-center' : ''}`}>
          <button
            onClick={toggleDock}
            className={`flex items-center gap-3 rounded-2xl bg-teal-accent/[0.06] border border-teal-accent/[0.12] hover:bg-teal-accent/10 hover:border-teal-accent/20 transition-all ${
              !expanded ? 'w-10 h-10 justify-center' : 'w-full px-3 py-3'
            }`}
            title="Ask Ivy"
          >
            <div className="w-[38px] h-[38px] rounded-full bg-gray-800 border border-[rgba(167,139,250,0.20)] flex items-center justify-center shrink-0 animate-ivy-pulse">
              <GraspPodIvyIcon size={20} active />
            </div>
            {expanded && (
              <div className="text-left">
                <div className="text-[13px] font-semibold text-gray-100">Ask Ivy</div>
                <div className="text-[10px] text-gray-600">Voice or text</div>
              </div>
            )}
          </button>
        </div>

        {/* Collapse toggle — desktop only */}
        <button
          onClick={onCollapse}
          className="hidden lg:flex items-center justify-center py-3 border-t border-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </aside>
    </>
  )
}
